// THE PHONE DOCK (2026-11-02). One bar along the foot of every phone view, the home's bar, now
// shared: Home, News, Edition, the reader's first pinned tool, and More (every tool and page).
// The same five in the same places on the home, the News deck and the Edition, so the phone
// reads as one app with three rooms, not three sites. The surface that draws it says which room
// it is and takes the taps it can serve in place; the rest are plain links.
import type { MouseEvent } from "react";
import { Home, LayoutGrid, Newspaper, ScrollText } from "lucide-react";
import { TOOLS, type ToolId } from "./more/tools";
import { DEFAULT_DOCK } from "./more/favs";
import "./phonedock.css";

export type DockRoom = "home" | "news" | "edition" | ToolId;

export function PhoneDock({ here, favs, onTab, onMore }: {
  here: DockRoom | null;
  favs: ToolId[];
  /** Serve a tap in place (return true), or leave it to the link. */
  onTab?: (room: DockRoom, path: string) => boolean;
  onMore: () => void;
}) {
  const toolId = (favs.length ? favs : DEFAULT_DOCK)[0];
  const tool = TOOLS.find((t) => t.id === toolId);
  const items: { room: DockRoom; path: string; label: string; Icon: typeof Home }[] = [
    { room: "home", path: "/", label: "Home", Icon: Home },
    { room: "news", path: "/read", label: "News", Icon: Newspaper },
    { room: "edition", path: "/read#edition", label: "Edition", Icon: ScrollText },
    ...(tool ? [{ room: tool.id as DockRoom, path: tool.path, label: tool.short ?? tool.title, Icon: tool.icon as typeof Home }] : []),
  ];
  const tap = (room: DockRoom, path: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (onTab?.(room, path)) e.preventDefault();
  };
  return (
    <nav className="pdock" aria-label="InSnip">
      {items.map(({ room, path, label, Icon }) => (
        <a key={room} href={path} onClick={tap(room, path)} className={here === room ? "here" : undefined} aria-current={here === room ? "page" : undefined}>
          <Icon size={20} aria-hidden="true" />
          <span>{label}</span>
        </a>
      ))}
      <button type="button" onClick={onMore} aria-label="Every tool and page">
        <LayoutGrid size={20} aria-hidden="true" />
        <span>More</span>
      </button>
    </nav>
  );
}
